import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import MediaImageField from "@/components/common/MediaImageField";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useUnsavedGuard from "@/hooks/useUnsavedGuard";

function useSettingsSection(key) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/settings/${key}`);
      setData(data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [key]); // eslint-disable-line
  return { data, setData, loading, reload: load };
}

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-white/50">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-white/40">{hint}</p>}
  </div>
);

export default function Settings() {
  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure your workspace and security." />
      <Tabs defaultValue="general">
        <TabsList className="glass border-white/10 mb-6 flex-wrap h-auto">
          <TabsTrigger value="general" className="data-[state=active]:bg-white/10 data-[state=active]:text-white">General</TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-white/10 data-[state=active]:text-white">Email</TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-white/10 data-[state=active]:text-white">Security</TabsTrigger>
        </TabsList>
        <TabsContent value="general"><GeneralSettings /></TabsContent>
        <TabsContent value="email"><EmailSettings /></TabsContent>
        <TabsContent value="security"><SecuritySettings /></TabsContent>
      </Tabs>
    </div>
  );
}

function SaveBar({ onSave, dirty }) {
  return (
    <div className="mt-6 flex items-center justify-end gap-3">
      <span className={`text-xs ${dirty ? "text-amber-300" : "text-emerald-300/80"}`}>
        {dirty ? "Unsaved changes" : "All changes saved"}
      </span>
      <Button onClick={onSave} disabled={!dirty} className="btn-gradient text-white border-0"><Icons.Save className="w-4 h-4 mr-1.5" /> Save changes</Button>
    </div>
  );
}

function GeneralSettings() {
  const { data, setData, loading } = useSettingsSection("general");
  const { dirty, markClean } = useUnsavedGuard(data, { ready: !loading });
  const save = async () => {
    try { await api.patch("/settings/general", data); toast.success("Saved"); markClean(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <GlassCard className="p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Company Name"><Input value={data.company_name || ""} onChange={(e) => setData({ ...data, company_name: e.target.value })} className="bg-black/20 border-white/10 text-white" /></Field>
        <Field label="Tagline"><Input value={data.tagline || ""} onChange={(e) => setData({ ...data, tagline: e.target.value })} className="bg-black/20 border-white/10 text-white" /></Field>
        <Field label="Contact Email"><Input value={data.contact_email || ""} onChange={(e) => setData({ ...data, contact_email: e.target.value })} className="bg-black/20 border-white/10 text-white" /></Field>
        <Field label="Contact Phone"><Input value={data.contact_phone || ""} onChange={(e) => setData({ ...data, contact_phone: e.target.value })} className="bg-black/20 border-white/10 text-white" /></Field>
        <Field label="Address" hint="Workspace setting only — public footer uses Brand & Pages → Contact">
          <Input value={data.address || ""} onChange={(e) => setData({ ...data, address: e.target.value })} className="bg-black/20 border-white/10 text-white" />
        </Field>
      </div>
      <MediaImageField
        label="Logo"
        value={data.logo_url || ""}
        onChange={(url) => setData({ ...data, logo_url: url })}
        folder="brand"
        aspectClass="h-28"
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
        {["twitter", "linkedin", "instagram", "youtube"].map(s => (
          <Field key={s} label={s}>
            <Input
              value={data.social?.[s] || ""}
              onChange={(e) => setData({ ...data, social: { ...(data.social || {}), [s]: e.target.value } })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
        ))}
      </div>
      <SaveBar onSave={save} dirty={dirty} />
    </GlassCard>
  );
}

function EmailSettings() {
  const { data, setData, loading } = useSettingsSection("email");
  const { dirty, markClean } = useUnsavedGuard(data, { ready: !loading });
  const save = async () => {
    try { await api.patch("/settings/email", data); toast.success("Saved"); markClean(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <GlassCard className="p-6 space-y-4">
      <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#FCD34D]">
        <Icons.AlertCircle className="w-4 h-4" />
        Add your SendGrid API key below to enable transactional emails (lead replies, 2FA OTP, notifications).
      </div>
      <Field label="SMTP Provider">
        <Input value={data.smtp_provider || "sendgrid"} onChange={(e) => setData({ ...data, smtp_provider: e.target.value })} className="bg-black/20 border-white/10 text-white" />
      </Field>
      <Field label="SendGrid API Key" hint="Starts with SG.">
        <Input type="password" value={data.sendgrid_api_key || ""} onChange={(e) => setData({ ...data, sendgrid_api_key: e.target.value })} className="bg-black/20 border-white/10 text-white" placeholder="SG.xxxxx" />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Sender Email"><Input value={data.sender_email || ""} onChange={(e) => setData({ ...data, sender_email: e.target.value })} className="bg-black/20 border-white/10 text-white" placeholder="hello@yourdomain.com" /></Field>
        <Field label="Sender Name"><Input value={data.sender_name || ""} onChange={(e) => setData({ ...data, sender_name: e.target.value })} className="bg-black/20 border-white/10 text-white" /></Field>
      </div>
      <SaveBar onSave={save} dirty={dirty} />
    </GlassCard>
  );
}

function SecuritySettings() {
  const { data, setData, loading } = useSettingsSection("security");
  const { dirty, markClean } = useUnsavedGuard(data, { ready: !loading });
  const save = async () => {
    try { await api.patch("/settings/security", data); toast.success("Saved"); markClean(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <GlassCard className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-white">Require Two-Factor Authentication</div>
          <div className="text-xs text-white/50">All admins must enable 2FA on next login.</div>
        </div>
        <Switch checked={!!data.two_factor_required} onCheckedChange={(v) => setData({ ...data, two_factor_required: v })} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Session Timeout (minutes)"><Input type="number" value={data.session_timeout_minutes ?? 720} onChange={(e) => setData({ ...data, session_timeout_minutes: Number(e.target.value) })} className="bg-black/20 border-white/10 text-white" /></Field>
        <Field label="Password Minimum Length"><Input type="number" value={data.password_min_length ?? 8} onChange={(e) => setData({ ...data, password_min_length: Number(e.target.value) })} className="bg-black/20 border-white/10 text-white" /></Field>
        <Field label="IP Whitelist (comma)"><Input value={(data.ip_whitelist || []).join(", ")} onChange={(e) => setData({ ...data, ip_whitelist: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-black/20 border-white/10 text-white" /></Field>
        <Field label="IP Blacklist (comma)"><Input value={(data.ip_blacklist || []).join(", ")} onChange={(e) => setData({ ...data, ip_blacklist: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-black/20 border-white/10 text-white" /></Field>
      </div>
      <SaveBar onSave={save} dirty={dirty} />
    </GlassCard>
  );
}
